import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FluxButton,
  FluxFind,
  FluxDropdown,
  FluxList,
  FluxListItem,
  FluxToolbar,
} from '@meltwater/flux-web-components-react';
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@mui/material';
import {
  History as HistoryIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import searchHistoryUtils from '../../utils/searchHistory';

const LandingPageSearch = () => {
  const navigate = useNavigate();
  const [findvalue, setFindvalue] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const searchRef = useRef(null);
  const historyRef = useRef(null);

  // Load search history on component mount
  useEffect(() => {
    setSearchHistory(searchHistoryUtils.getHistory());
  }, []);

  useEffect(() => {
    const handleClickAway = (event) => {
      if (
        searchRef.current && 
        !searchRef.current.contains(event.target) &&
        historyRef.current && 
        !historyRef.current.contains(event.target)
      ) {
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, []);

  // Function to validate ticket format - allow any input with a dash for future flexibility
  const isValidTicket = (value) => {
    return value.includes('-');
  };

  // Function to detect search type automatically
  const detectSearchType = (value) => {
    if (!value || value.trim() === '') return null;
    
    const trimmedValue = value.trim();
  
    if (isValidTicket(trimmedValue)) {
      return 'ticket';
    }
    
    // Check if it's a Salesforce ID (15-18 characters, alphanumeric)
    const salesforcePattern = /^[a-zA-Z0-9]{15,18}$/;
    if (salesforcePattern.test(trimmedValue) && trimmedValue.length >= 15) {
      return 'salesforce';
    }
    
    // Check if it's numeric (Account/Opportunity ID)
    const numericPattern = /^\d{5,}$/;
    if (numericPattern.test(trimmedValue)) {
      return 'account';
    }
    
    return null;
  };

  // Get the detected search type for current input
  const detectedType = detectSearchType(findvalue);

  const handleInputChange = (e) => {
    setFindvalue(e.target.value);
  };

  const handleSearch = (searchType) => {
    if (!findvalue.trim()) return;

    let actualSearchType, targetUrl;
    
    switch (searchType) {
      case 'OpportunityID':
        targetUrl = `/jira/relatedtickets/${findvalue.trim()}/OpportunityID`;
        actualSearchType = 'opportunity';
        break;
      case 'AccountID':
        targetUrl = `/jira/relatedtickets/${findvalue.trim()}/AccountID`;
        actualSearchType = 'account';
        break;
      case 'SalesforceId':
        targetUrl = `/jira/salesforce/${findvalue.trim()}`;
        actualSearchType = 'salesforce';
        break;
      case 'ticket':
        targetUrl = `/jira/${findvalue.trim()}`;
        actualSearchType = 'ticket';
        break;
      default:
        // Auto-detect if no specific type provided
        const detectedType = detectSearchType(findvalue);
        if (!detectedType) return;
        
        if (detectedType === 'ticket') {
          targetUrl = `/jira/${findvalue.trim()}`;
          actualSearchType = 'ticket';
        } else if (detectedType === 'salesforce') {
          targetUrl = `/jira/salesforce/${findvalue.trim()}`;
          actualSearchType = 'salesforce';
        } else if (detectedType === 'account') {
          targetUrl = `/jira/relatedtickets/${findvalue.trim()}/AccountID`;
          actualSearchType = 'account';
        } else {
          return;
        }
        break;
    }

    // Save to history
    searchHistoryUtils.addToHistory(actualSearchType, findvalue.trim());
    setSearchHistory(searchHistoryUtils.getHistory());

    navigate(targetUrl);
  };

  const handleHistoryItemClick = (historyItem) => {
    setFindvalue(historyItem.value);
    setShowHistory(false);
    
    // Update access time and reorder history
    const updatedHistory = searchHistoryUtils.updateAccessTime(historyItem.id);
    setSearchHistory(updatedHistory);
    
    // Navigate directly using the stored URL
    navigate(historyItem.searchUrl);
  };

  const handleSearchInputFocus = () => {
    if (searchHistory.length > 0) {
      setShowHistory(true);
    }
  };

  const handleSearchInputBlur = (e) => {
    // Add a small delay to allow for dropdown clicks
    setTimeout(() => {
      if (!historyRef.current?.contains(document.activeElement)) {
        setShowHistory(false);
      }
    }, 150);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const searchType = detectSearchType(findvalue);
      if (searchType) {
        handleSearch(searchType);
      }
    }
  };



  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: '600px', margin: '0 auto' }}>
      <div ref={searchRef}>
        <FluxToolbar style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <FluxFind
            style={{ 
              border: '2px solid #28bcbb',
              borderRadius: '10px',
              padding: '8px 16px',
              outline: 'none',
              transition: 'all 0.3s ease',
              boxShadow: '0 2px 8px rgba(40, 188, 187, 0.15)',
              backgroundColor: '#ffffff',
              flex: '1',
              minWidth: '300px'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1e9b9b';
              e.target.style.boxShadow = '0 4px 12px rgba(40, 188, 187, 0.25)';
              handleSearchInputFocus(e);
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#28bcbb';
              e.target.style.boxShadow = '0 2px 8px rgba(40, 188, 187, 0.15)';
              handleSearchInputBlur(e);
            }}
            value={findvalue}
            onInput={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Find..."
          />
          <FluxDropdown hoistMenu>
            <FluxButton type='filled' slot='trigger' tint='primary' caret>
              Search By
            </FluxButton>
            <FluxList>
              <FluxListItem>
                <FluxButton
                  disabled={detectedType !== 'ticket'}
                  tint='primary'
                  type='text'
                  onClick={() => handleSearch('ticket')}
                  id='ticket'
                >
                  Ticket {detectedType === 'ticket' ? '✓' : ''}
                </FluxButton>
              </FluxListItem>
              <FluxListItem>
                <FluxButton
                  disabled={detectedType !== 'account'}
                  tint='primary'
                  type='text'
                  onClick={() => handleSearch('AccountID')}
                  id='AccountID'
                >
                  Account ID {detectedType === 'account' ? '✓' : ''}
                </FluxButton>
              </FluxListItem>
              <FluxListItem>
                <FluxButton
                  disabled={detectedType !== 'account'}
                  tint='primary'
                  type='text'
                  onClick={() => handleSearch('OpportunityID')}
                  id='OpportunityID'
                >
                  Opportunity ID {detectedType === 'account' ? '✓' : ''}
                </FluxButton>
              </FluxListItem>
              <FluxListItem>
                <FluxButton
                  disabled={detectedType !== 'salesforce'}
                  tint='primary'
                  type='text'
                  onClick={() => handleSearch('SalesforceId')}
                  id='SalesforceId'
                >
                  Salesforce ID {detectedType === 'salesforce' ? '✓' : ''}
                </FluxButton>
              </FluxListItem>
            </FluxList>
          </FluxDropdown>
        </FluxToolbar>
      </div>

      {showHistory && searchHistory.length > 0 && (
        <Paper
          ref={historyRef}
          elevation={8}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            mt: 1,
            maxHeight: '300px',
            overflow: 'hidden',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              pb: 1,
              background: 'grey.50',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" color="text.secondary">
                Recent Searches
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setShowClearConfirm(true)}
              title="Clear all history"
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          </Box>

          <Divider />

          {/* Scrollable History List */}
          <List
            sx={{
              maxHeight: '250px',
              overflow: 'auto',
              py: 0
            }}
          >
            {searchHistory.map((item) => (
              <ListItem
                key={item.id}
                disablePadding
                secondaryAction={
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setItemToDelete(item.id);
                    }}
                    title="Remove from history"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                }
              >
                <ListItemButton
                  onClick={() => handleHistoryItemClick(item)}
                  sx={{ pr: 6 }}
                >
                  <ListItemText
                    primary={`${item.type.charAt(0).toUpperCase() + item.type.slice(1)}: ${item.value}`}
                    secondary={searchHistoryUtils.getTimeAgo(item.timestamp)}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: 500
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem'
                    }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={showClearConfirm}
        onClose={() => setShowClearConfirm(false)}
        aria-labelledby="clear-dialog-title"
        aria-describedby="clear-dialog-description"
      >
        <DialogTitle id="clear-dialog-title">
          Clear Search History
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="clear-dialog-description">
            Are you sure you want to clear all search history? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <FluxButton type="text" onClick={() => setShowClearConfirm(false)}>
            Cancel
          </FluxButton>
          <FluxButton 
            type="filled" 
            tint="danger" 
            onClick={() => {
              searchHistoryUtils.clearHistory();
              setSearchHistory([]);
              setShowHistory(false);
              setShowClearConfirm(false);
            }}
          >
            Clear All
          </FluxButton>
        </DialogActions>
      </Dialog>

      {/* Delete Item Confirmation Dialog */}
      <Dialog
        open={Boolean(itemToDelete)}
        onClose={() => setItemToDelete(null)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Remove Search Item
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to remove this item from your search history?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <FluxButton type="text" onClick={() => setItemToDelete(null)}>
            Cancel
          </FluxButton>
          <FluxButton 
            type="filled" 
            tint="danger" 
            onClick={() => {
              if (itemToDelete) {
                searchHistoryUtils.removeFromHistory(itemToDelete);
                setSearchHistory(searchHistoryUtils.getHistory());
                setItemToDelete(null);
              }
            }}
          >
            Remove
          </FluxButton>
        </DialogActions>
      </Dialog>
      
    </Box>
  );
};

export default LandingPageSearch;